import { useRef, useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MentionTextarea } from "./mention-textarea";
import { Linkify } from "./linkify";
import { CommentComparisonView } from "./comment-comparison-view";
import { ChevronDown, X, Edit3, Eye, Loader2, Paperclip, PencilLine, PencilOff, Send, X as XIcon, Mic, Sparkles, GripVertical } from "lucide-react";
import type { PageIndicator, DocumentUser, CommentAttachment, IndicatorComment } from "@shared/schema";
import { useAudioRecorder } from "@/hooks/use-audio-recorder";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

type CommentWithUser = IndicatorComment & { user: DocumentUser; attachments: CommentAttachment[] };

const STATUS_OPTIONS = [
  { value: "open", label: "Open", color: "bg-amber-500/80" },
  { value: "inProgress", label: "In Progress", color: "bg-sky-500/80" },
  { value: "resolved", label: "Resolved", color: "bg-emerald-500/80" },
  { value: "skipped", label: "Skipped", color: "bg-neutral-500" },
] as const;

interface CommentThreadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeIndicator: PageIndicator | null;
  pendingIndicator: { x: number; y: number; canvasX: number; canvasY: number; number: number; } | null;
  indicatorColor: string;
  comments: CommentWithUser[];
  isLoadingComments: boolean;
  currentUser: { id: string; name: string; email: string } | null;
  documentUsers: DocumentUser[];
  newComment: string;
  setNewComment: (value: string) => void;
  selectedImages: Array<{
    id: string;
    file?: File;
    url?: string;
    storageUrl?: string;
    storageThumbnailUrl?: string;
    status: 'uploading' | 'uploaded' | 'error';
    originalFilename: string;
  }>;
  setSelectedImages: React.Dispatch<React.SetStateAction<CommentThreadDialogProps['selectedImages']>>;
  isDragActive: boolean;
  setIsDragActive: (active: boolean) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  commentsScrollRef: React.RefObject<HTMLDivElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  editingCommentId: string | null;
  setEditingCommentId: (id: string | null) => void;
  editingCommentText: string;
  setEditingCommentText: (text: string) => void;
  indicatorDrawingBoxes: Map<number, any>;
  onStatusChange: (indicatorId: string, status: string) => void;
  onClose: () => Promise<void>;
  onSubmitComment: (content: string) => void;
  onMentionSelect: (user: DocumentUser) => void;
  onDeleteComment: (commentId: string) => void;
  onEditComment: (commentId: string, content: string) => void;
  onImageUpload: (file: File) => Promise<{ thumbnailUrl: string; storageUrl: string; storageThumbnailUrl: string }>;
  onRemoveImage: (id: string) => void;
  onStartDrawing: () => void;
  onClearDrawing: () => void;
  onLightboxOpen: (url: string, filename: string) => void;
  deleteCommentPending: boolean;
  onImproveComment?: () => void;
  isImprovingComment?: boolean;
  improvedCommentSuggestion?: string | null;
  onAcceptImprovement?: () => void;
  onRejectImprovement?: () => void;
}

export function CommentThreadDialog({
  open,
  onOpenChange,
  activeIndicator,
  pendingIndicator,
  indicatorColor,
  comments,
  isLoadingComments,
  currentUser,
  documentUsers,
  newComment,
  setNewComment,
  selectedImages,
  setSelectedImages,
  isDragActive,
  setIsDragActive,
  textareaRef,
  commentsScrollRef,
  fileInputRef,
  editingCommentId,
  setEditingCommentId,
  editingCommentText,
  setEditingCommentText,
  indicatorDrawingBoxes,
  onStatusChange,
  onClose,
  onSubmitComment,
  onMentionSelect,
  onDeleteComment,
  onEditComment,
  onImageUpload,
  onRemoveImage,
  onStartDrawing,
  onClearDrawing,
  onLightboxOpen,
  deleteCommentPending,
  onImproveComment,
  isImprovingComment = false,
  improvedCommentSuggestion = null,
  onAcceptImprovement,
  onRejectImprovement,
}: CommentThreadDialogProps) {
  const dragCounterRef = useRef(0);
  const { toast } = useToast();
  
  const { recordingState, startRecording, stopRecording, cancelRecording } = useAudioRecorder();
  const [isTranscribing, setIsTranscribing] = useState(false);

  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dialogElRef = useRef<HTMLDivElement>(null);
  const isStatusSelectOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      dragOffsetRef.current = { x: 0, y: 0 };
      const el = dialogElRef.current;
      if (el) {
        el.style.left = '50%';
        el.style.top = '50%';
      }
    }
  }, [open]);

  const handleDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, select, [role="combobox"]')) return;
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: dragOffsetRef.current.x,
      offsetY: dragOffsetRef.current.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handleDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    dragOffsetRef.current = {
      x: dragStartRef.current.offsetX + dx,
      y: dragStartRef.current.offsetY + dy,
    };
    const el = dialogElRef.current;
    if (el) {
      el.style.left = `calc(50% + ${dragOffsetRef.current.x}px)`;
      el.style.top = `calc(50% + ${dragOffsetRef.current.y}px)`;
    }
  }, []);

  const handleDragEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsDragging(false);
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const transcribeMutation = useMutation({
    mutationFn: async (audioBlob: Blob) => {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const response = await fetch('/api/transcription/transcribe', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Transcription failed');
      }
      
      const data = await response.json();
      return data.text as string;
    },
    onSuccess: (transcribedText) => {
      if (transcribedText) {
        const trimmedText = transcribedText.trim();
        if (trimmedText) {
          const existingText = newComment.trim();
          if (existingText) {
            setNewComment(`${existingText} ${trimmedText}`);
          } else {
            setNewComment(trimmedText);
          }
        }
      }
      setIsTranscribing(false);
    },
    onError: (error) => {
      console.error('Transcription error:', error);
      toast({
        title: "Transcription failed",
        description: "Failed to transcribe audio. Please try again.",
        variant: "destructive",
      });
      setIsTranscribing(false);
    },
  });

  const handleMicrophoneClick = async () => {
    if (recordingState === 'recording') {
      setIsTranscribing(true);
      const audioBlob = await stopRecording();
      if (audioBlob) {
        transcribeMutation.mutate(audioBlob);
      } else {
        setIsTranscribing(false);
      }
    } else if (recordingState === 'idle' && !isTranscribing) {
      try {
        await startRecording();
      } catch (error) {
        console.error('Failed to start recording:', error);
        toast({
          title: "Microphone access denied",
          description: "Please allow microphone access to use voice input.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDialogClose = async () => {
    if (recordingState === 'recording') {
      cancelRecording();
    }
    await onClose();
  };
  
  const indicator = activeIndicator || pendingIndicator;
  if (!indicator) return null;

  const hasDrawingBox = activeIndicator 
    ? !!activeIndicator.drawingBox 
    : pendingIndicator 
      ? indicatorDrawingBoxes.has(pendingIndicator.number)
      : false;

  return (
    <Dialog 
      open={open} 
      onOpenChange={async (isOpen) => {
        if (!isOpen) {
          await handleDialogClose();
        }
      }}
    >
      <DialogContent 
        className="w-[calc(100vw-32px)] max-w-[320px] md:max-w-[370px] lg:max-w-[415px] p-0 border border-neutral-200/70 dark:border-neutral-700/40 shadow-2xl bg-neutral-50 dark:bg-[#141414] backdrop-blur-xl [&>button]:hidden rounded-lg overflow-hidden focus:outline-none focus:ring-0"
        overlayClassName={isDragging ? "!backdrop-blur-none" : undefined}
        ref={dialogElRef}
        style={{
          boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.08)',
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef.current?.focus();
        }}
        onPointerDownOutside={async (e) => {
          // Don't close while the status Select dropdown is open
          if (isStatusSelectOpenRef.current) {
            e.preventDefault();
            return;
          }
          const originalEvent = (e as any).detail?.originalEvent;
          const target = (originalEvent?.target || e.target) as HTMLElement;
          const inputSection = target.closest('[data-input-section]');
          if (inputSection) {
            e.preventDefault();
          } else {
            await handleDialogClose();
          }
        }}
        onOverlayPointerDown={handleDialogClose}
      >
        <VisuallyHidden>
          <DialogTitle>Comment Thread</DialogTitle>
          <DialogDescription>View and add comments for this indicator</DialogDescription>
        </VisuallyHidden>
        
        <div className="flex flex-col overflow-hidden rounded-lg">
          {/* Header */}
          <div
            className="flex items-center justify-between px-3.5 py-2.5 border-b border-neutral-200/60 dark:border-neutral-800/80 bg-neutral-50 dark:bg-[#1a1a1a] cursor-grab active:cursor-grabbing select-none touch-none"
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
          >
            <div className="flex items-center gap-2.5">
              <GripVertical className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
              <div 
                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold shadow-sm ring-1 ring-black/10 dark:ring-white/10"
                style={{ backgroundColor: indicatorColor }}
              >
                {indicator.number}
              </div>
              {pendingIndicator && (
                <span className="text-[10px] font-medium tracking-wide uppercase px-1.5 py-0.5 rounded bg-neutral-200/80 dark:bg-neutral-700/60 text-neutral-500 dark:text-neutral-400">
                  unsaved
                </span>
              )}
            </div>
            {!pendingIndicator && activeIndicator && (
              <div className="flex items-center gap-1.5" onPointerDown={(e) => e.stopPropagation()}>
                <span className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 font-medium">Status</span>
                <Select
                  value={activeIndicator.status || "open"}
                  onValueChange={(status) => onStatusChange(activeIndicator.id, status)}
                  onOpenChange={(open) => { isStatusSelectOpenRef.current = open; }}
                >
                  <SelectTrigger 
                    tabIndex={-1}
                    className="w-5 h-5 p-0 border-0 rounded-full bg-transparent hover:scale-110 transition-transform relative [&>svg]:hidden"
                  >
                    <div 
                      className={`w-4 h-4 rounded-full relative flex items-center justify-center ${
                        STATUS_OPTIONS.find(opt => opt.value === (activeIndicator.status || "open"))?.color || "bg-amber-500/80"
                      }`}
                    >
                      <ChevronDown className="w-3 h-3 text-white opacity-80" />
                    </div>
                  </SelectTrigger>
                  <SelectContent 
                    className="bg-white dark:bg-[#1a1a1a] border-neutral-200/60 dark:border-neutral-700/40"
                    onCloseAutoFocus={(e) => {
                      e.preventDefault();
                      textareaRef.current?.focus();
                    }}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value} className="cursor-pointer text-sm">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${option.color}`}></div>
                          <span className="text-neutral-700 dark:text-neutral-300">{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  data-testid="button-close-dialog"
                  onPointerDown={onClose}
                  className="w-5 h-5 flex items-center justify-center rounded text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                  title="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Comments Section */}
          {isLoadingComments && comments.length === 0 ? (
            <div className="p-4 text-center bg-white/60 dark:bg-[#111111]">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-3 h-3 text-neutral-400 dark:text-neutral-600 animate-spin" />
                <span className="text-[11px] text-neutral-400 dark:text-neutral-600 tracking-wide font-mono">Loading...</span>
              </div>
            </div>
          ) : comments.length > 0 ? (
            <ScrollArea ref={commentsScrollRef} className="max-h-64 md:max-h-80 lg:max-h-96 bg-white/60 dark:bg-[#111111]">
              <div className="p-3 space-y-0">
                {comments.map((comment, index) => {
                  const commentDate = new Date(comment.createdAt);
                  const now = new Date();
                  const diffInHours = Math.floor((now.getTime() - commentDate.getTime()) / (1000 * 60 * 60));
                  const diffInDays = Math.floor(diffInHours / 24);
                  
                  let timeDisplay;
                  if (diffInHours < 1) {
                    timeDisplay = "now";
                  } else if (diffInHours < 24) {
                    timeDisplay = `${diffInHours}h`;
                  } else if (diffInDays < 7) {
                    timeDisplay = `${diffInDays}d`;
                  } else {
                    timeDisplay = commentDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
                  }
                  
                  return (
                    <div key={comment.id} className="group relative py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-500">
                          <span className="font-medium text-neutral-600 dark:text-neutral-400">{comment.user?.name || 'Anonymous'}</span>
                          <span className="mx-1 text-neutral-300 dark:text-neutral-600">/</span>
                          <span className="font-mono tabular-nums">{timeDisplay}</span>
                        </span>
                        <div className="flex items-center gap-0.5">
                          {currentUser && comment.userId && currentUser.id === comment.userId && (
                            <>
                              <button
                                className="w-5 h-5 sm:w-4 sm:h-4 flex items-center justify-center rounded text-neutral-400 dark:text-neutral-600 hover:text-neutral-700 dark:hover:text-neutral-300 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingCommentId(comment.id);
                                  setEditingCommentText(comment.content);
                                }}
                              >
                                <Edit3 className="w-2.5 h-2.5 sm:w-2 sm:h-2" />
                              </button>
                              <button
                                className="w-5 h-5 sm:w-4 sm:h-4 flex items-center justify-center rounded text-neutral-400 dark:text-neutral-600 hover:text-red-500 dark:hover:text-red-400 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteComment(comment.id);
                                }}
                                disabled={deleteCommentPending}
                              >
                                <X className="w-2.5 h-2.5 sm:w-2 sm:h-2" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {editingCommentId === comment.id ? (
                        <textarea
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          className="w-full text-sm text-neutral-800 dark:text-neutral-100 bg-transparent border border-neutral-300 dark:border-neutral-600 rounded px-2 py-1 resize-none outline-none focus:ring-1 focus:ring-neutral-400 dark:focus:ring-neutral-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              e.stopPropagation();
                              if (editingCommentText.trim() !== comment.content) {
                                onEditComment(comment.id, editingCommentText.trim());
                              } else {
                                setEditingCommentId(null);
                                setEditingCommentText("");
                              }
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingCommentId(null);
                              setEditingCommentText("");
                            }
                          }}
                          onBlur={() => {
                            if (editingCommentText.trim() !== comment.content) {
                              onEditComment(comment.id, editingCommentText.trim());
                            } else {
                              setEditingCommentId(null);
                              setEditingCommentText("");
                            }
                          }}
                          rows={Math.min(Math.ceil(editingCommentText.length / 40), 4)}
                        />
                      ) : (
                        <>
                          <div className="text-[13px] text-neutral-700 dark:text-neutral-200 leading-relaxed whitespace-pre-wrap break-words select-text cursor-text">
                            <Linkify>{comment.content}</Linkify>
                          </div>
                          
                          {comment.attachments && comment.attachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {comment.attachments.map((attachment) => (
                                <div 
                                  key={attachment.id} 
                                  className="relative group cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onLightboxOpen(attachment.storageUrl, attachment.originalFilename);
                                  }}
                                >
                                  <img
                                    src={attachment.thumbnailUrl}
                                    alt={attachment.originalFilename}
                                    className="w-14 h-14 object-cover rounded border border-neutral-200 dark:border-neutral-700 hover:border-neutral-400 dark:hover:border-neutral-500 transition-colors"
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded transition-colors flex items-center justify-center pointer-events-none">
                                    <Eye className="w-3.5 h-3.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                      
                      {index < comments.length - 1 && (
                        <div className="h-px bg-neutral-200/60 dark:bg-neutral-800 mt-2"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className="p-4 text-center bg-white/60 dark:bg-[#111111]">
              {pendingIndicator && (
                <div className="text-[11px] tracking-wide text-neutral-400 dark:text-neutral-600">
                  Add a comment to save
                </div>
              )}
            </div>
          )}

          {/* Input Section */}
          <div 
            className="border-t border-neutral-200/60 dark:border-neutral-800/80 bg-neutral-100/80 dark:bg-[#161616]"
            data-input-section
          >
            <div 
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDragEnter={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                dragCounterRef.current++;
                if (dragCounterRef.current === 1) {
                  setIsDragActive(true);
                }
              }}
              onDragLeave={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                dragCounterRef.current--;
                if (dragCounterRef.current === 0) {
                  setIsDragActive(false);
                }
              }}
              onDrop={(e) => {
                dragCounterRef.current = 0;
                setIsDragActive(false);
                e.preventDefault();
                e.stopPropagation();
                const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                files.slice(0, 5).forEach(file => {
                  const imageId = Math.random().toString(36).substring(2, 15);
                  setSelectedImages(prev => [...prev, {
                    id: imageId,
                    file,
                    status: 'uploading',
                    originalFilename: file.name
                  }]);
                  onImageUpload(file)
                    .then((result) => {
                      setSelectedImages(prev => prev.map(img => 
                        img.id === imageId 
                          ? { 
                              ...img, 
                              status: 'uploaded', 
                              url: result.thumbnailUrl, 
                              storageUrl: result.storageUrl,
                              storageThumbnailUrl: result.storageThumbnailUrl,
                              file: undefined 
                            }
                          : img
                      ));
                    })
                    .catch(() => {
                      setSelectedImages(prev => prev.map(img => 
                        img.id === imageId 
                          ? { ...img, status: 'error' }
                          : img
                      ));
                    });
                });
              }}
              className={`p-3 transition-colors relative ${isDragActive ? 'bg-neutral-200/40 dark:bg-neutral-800/30' : ''}`}
            >
              {selectedImages.length > 0 && (
                <div className="mb-2.5 flex flex-wrap gap-1.5">
                  {selectedImages.map((image) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.url || (image.file ? URL.createObjectURL(image.file) : '')}
                        alt={image.originalFilename}
                        className="w-14 h-14 object-cover rounded border border-neutral-200 dark:border-neutral-700"
                      />
                      
                      {image.status === 'uploading' && (
                        <div className="absolute inset-0 bg-black/50 rounded flex items-center justify-center">
                          <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                        </div>
                      )}
                      
                      {image.status === 'error' && (
                        <div className="absolute inset-0 bg-red-500/40 rounded flex items-center justify-center">
                          <XIcon className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveImage(image.id);
                        }}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-neutral-600 dark:bg-neutral-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XIcon className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="space-y-2.5">
                {improvedCommentSuggestion && onAcceptImprovement && onRejectImprovement && (
                  <CommentComparisonView
                    originalComment={newComment}
                    improvedComment={improvedCommentSuggestion}
                    onAccept={onAcceptImprovement}
                    onReject={onRejectImprovement}
                    indicatorColor={indicatorColor}
                  />
                )}
                
                <div 
                  className="relative" 
                  style={{ touchAction: 'auto', WebkitUserSelect: 'text', userSelect: 'text' }}
                >
                  <MentionTextarea
                    ref={textareaRef}
                    data-comment-textarea
                    data-testid="comment-textarea"
                    value={newComment}
                    onChange={setNewComment}
                    onMentionSelect={onMentionSelect}
                    users={documentUsers}
                    placeholder="Add comment..."
                    className="w-full text-[13px] bg-transparent border-0 resize-none outline-none placeholder-neutral-400 dark:placeholder-neutral-600 text-neutral-800 dark:text-neutral-200"
                    minRows={3}
                    maxRows={10}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        if (newComment.trim() || selectedImages.length > 0) {
                          onSubmitComment(newComment.trim());
                        }
                      }
                    }}
                  />
                </div>
                
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-shrink-0">
                    {newComment.trim() && onImproveComment && (
                      <button
                        onClick={onImproveComment}
                        disabled={isImprovingComment}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium tracking-wide bg-neutral-200/70 dark:bg-[#1e1e1e] text-neutral-500 dark:text-neutral-400 hover:bg-neutral-300/70 dark:hover:bg-[#252525] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="button-improve-comment"
                      >
                        {isImprovingComment ? (
                          <>
                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                            <span>Improving...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-2.5 w-2.5" />
                            <span>Improve with AI</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 ml-auto">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(file => {
                        const imageId = Math.random().toString(36).substring(2, 15);
                        setSelectedImages(prev => [...prev, {
                          id: imageId,
                          file,
                          status: 'uploading',
                          originalFilename: file.name
                        }]);
                        onImageUpload(file)
                          .then((result) => {
                            setSelectedImages(prev => prev.map(img => 
                              img.id === imageId 
                                ? { 
                                    ...img, 
                                    status: 'uploaded', 
                                    url: result.thumbnailUrl, 
                                    storageUrl: result.storageUrl,
                                    storageThumbnailUrl: result.storageThumbnailUrl,
                                    file: undefined 
                                  }
                                : img
                            ));
                          })
                          .catch(() => {
                            setSelectedImages(prev => prev.map(img => 
                              img.id === imageId 
                                ? { ...img, status: 'error' }
                                : img
                            ));
                          });
                      });
                      e.target.value = '';
                    }}
                  />
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    title="Attach images"
                    className="h-7 w-7 flex-shrink-0 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-200/60 dark:hover:bg-[#1e1e1e]"
                    data-testid="button-attach"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      if (hasDrawingBox) {
                        onClearDrawing();
                      } else {
                        onStartDrawing();
                      }
                    }}
                    title={hasDrawingBox ? "Clear drawing box" : "Draw box around elements"}
                    className="h-7 w-7 flex-shrink-0 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-200/60 dark:hover:bg-[#1e1e1e]"
                    data-testid={hasDrawingBox ? "button-clear-drawing" : "button-draw"}
                  >
                    {hasDrawingBox ? (
                      <PencilOff className="h-3.5 w-3.5" style={{ color: indicatorColor }} />
                    ) : (
                      <PencilLine className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleMicrophoneClick}
                    disabled={isTranscribing}
                    title={
                      isTranscribing ? "Processing audio..." :
                      recordingState === 'recording' ? "Stop recording" :
                      "Record voice comment"
                    }
                    className="h-7 w-7 flex-shrink-0 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-200/60 dark:hover:bg-[#1e1e1e]"
                    data-testid={recordingState === 'recording' ? "button-stop-recording" : "button-start-recording"}
                  >
                    {isTranscribing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />
                    ) : recordingState === 'recording' ? (
                      <Mic className="h-3.5 w-3.5 animate-pulse" style={{ color: '#ef4444' }} />
                    ) : (
                      <Mic className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  
                    {(newComment.trim() || selectedImages.length > 0) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (newComment.trim() || selectedImages.length > 0) {
                            onSubmitComment(newComment.trim());
                          }
                        }}
                        disabled={
                          (!newComment.trim() && selectedImages.length === 0) ||
                          selectedImages.some(img => img.status === 'uploading')
                        }
                        title={
                          selectedImages.some(img => img.status === 'uploading') ? "Uploading images..." :
                          "Send comment (⌘+Enter)"
                        }
                        className="h-7 w-7 flex-shrink-0 hover:bg-neutral-200/60 dark:hover:bg-[#1e1e1e]"
                        data-testid="button-send"
                      >
                        {selectedImages.some(img => img.status === 'uploading') ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />
                        ) : (
                          <Send className="h-3.5 w-3.5" style={{ color: indicatorColor }} />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {isDragActive && (
                <div 
                  className="absolute inset-0 border border-dashed rounded flex items-center justify-center bg-neutral-100/90 dark:bg-[#1a1a1a]/90"
                  style={{ borderColor: `${indicatorColor}60` }}
                >
                  <div className="text-[11px] font-medium tracking-wide text-neutral-500 dark:text-neutral-400">
                    Drop images here
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
